import React, { useState } from "react";
import {
  Search,
  User,
  Briefcase,
  Calendar,
  DollarSign,
  X,
  RefreshCw,
} from "lucide-react";
import { useHistory } from "react-router-dom";

export default function BossShowEmployee({ onRefresh, refreshing, allData }) {
  const history = useHistory();
  const [searchQuery, setSearchQuery] = useState("");

  const getSalaryDisplay = (item) => {
    // TypeSalary: 1 = Daily, 2 = Monthly
    if (item.typeSalary === 2) {
      return {
        amount: item.monthlySalary || 0,
        type: "Monthly",
        icon: Calendar,
        color: "#9C27B0",
      };
    } else {
      return {
        amount: item.salary || 0,
        type: "Daily",
        icon: Calendar,
        color: "#4CAF50",
      };
    }
  };

  // Filter employees based on search query
  const getFilteredEmployees = () => {
    const activeEmployees =
      allData?.myEmployees?.filter((item) => item.active === 1) || [];

    if (!searchQuery.trim()) {
      return activeEmployees;
    }

    return activeEmployees.filter((employee) =>
      employee.name.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  };

  const handleEmployeeClick = (employee) => {
    // Store employee data in localStorage for the statistics page
    localStorage.setItem("choosedEmployee", JSON.stringify(employee));

    // Store allData for statistics page to use
    localStorage.setItem("allData", JSON.stringify(allData));

    // Navigate to employee statistics page
    history.push(`/boss-employee-statistics/${employee.id}`);
  };

  const filteredEmployees = getFilteredEmployees();

  return (
    <div className="flex-1 h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white px-6 pt-6 pb-8 rounded-b-3xl shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">My Employees</h1>
            <p className="text-sm text-gray-600 mt-1">
              {filteredEmployees.length}{" "}
              {filteredEmployees.length === 1 ? "Employee" : "Employees"}
            </p>
          </div>
          <button
            onClick={onRefresh}
            disabled={refreshing}
            className="p-3 hover:bg-gray-100 rounded-xl transition disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw
              className={`w-5 h-5 text-gray-600 ${
                refreshing ? "animate-spin" : ""
              }`}
            />
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="px-6 py-4">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-12 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition shadow-sm"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full transition"
            >
              <X className="w-4 h-4 text-gray-400" />
            </button>
          )}
        </div>
      </div>

      {/* Employee Grid */}
      <div
        className="px-6 pb-24 overflow-y-auto"
        style={{ maxHeight: "calc(100vh - 280px)" }}
      >
        {filteredEmployees.length === 0 ? (
          // Empty State
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <User className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {searchQuery
                ? "No employees match your search"
                : "No Employees Found"}
            </h3>
            <p className="text-sm text-gray-500 text-center max-w-sm">
              {searchQuery
                ? "Try a different search term"
                : "Click the + button to add your first employee"}
            </p>
          </div>
        ) : (
          // Employee Cards Grid
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredEmployees.map((employee) => {
              const salaryInfo = getSalaryDisplay(employee);
              const SalaryIcon = salaryInfo.icon;

              return (
                <div
                  key={employee.id}
                  onClick={() => handleEmployeeClick(employee)}
                  className="bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition-all cursor-pointer border border-gray-100 hover:border-blue-200 group"
                >
                  {/* Avatar & Header */}
                  <div className="flex flex-col items-center mb-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                      <User className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="font-semibold text-gray-900 text-center text-base line-clamp-1">
                      {employee.name}
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">
                      ID: {employee.id}
                    </p>
                  </div>

                  {/* Type Badge */}
                  <div className="flex justify-center mb-4">
                    <div
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${
                        employee.type === 2
                          ? "bg-orange-50 text-orange-600"
                          : "bg-blue-50 text-blue-600"
                      }`}
                    >
                      {employee.type === 2 ? (
                        <Briefcase className="w-3.5 h-3.5" />
                      ) : (
                        <User className="w-3.5 h-3.5" />
                      )}
                      <span>
                        {employee.type === 2 ? "Manager" : "Employee"}
                      </span>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="border-t border-gray-100 mb-4"></div>

                  {/* Salary Info */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: salaryInfo.color + "15" }}
                      >
                        <SalaryIcon
                          className="w-4 h-4"
                          style={{ color: salaryInfo.color }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-500">
                          {salaryInfo.type}
                        </p>
                        <p
                          className="font-bold text-sm truncate"
                          style={{ color: salaryInfo.color }}
                        >
                          ${salaryInfo.amount.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* View Details Link */}
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <button className="w-full text-sm font-medium text-blue-600 hover:text-blue-700 transition">
                      View Statistics →
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Loading Overlay */}
      {refreshing && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-sm text-gray-600">Loading employees...</p>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
        .line-clamp-1 {
          overflow: hidden;
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 1;
        }
      `}</style>
    </div>
  );

  return (
    <div className="flex-1 h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white px-6 pt-6 pb-8 rounded-b-3xl shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">My Employees</h1>
            <p className="text-sm text-gray-600 mt-1">
              {filteredEmployees.length}{" "}
              {filteredEmployees.length === 1 ? "Employee" : "Employees"}
            </p>
          </div>
          <button
            onClick={onRefresh}
            disabled={refreshing}
            className="p-3 hover:bg-gray-100 rounded-xl transition disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw
              className={`w-5 h-5 text-gray-600 ${
                refreshing ? "animate-spin" : ""
              }`}
            />
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="px-6 py-4">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-12 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition shadow-sm"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full transition"
            >
              <X className="w-4 h-4 text-gray-400" />
            </button>
          )}
        </div>
      </div>

      {/* Employee Grid */}
      <div
        className="px-6 pb-24 overflow-y-auto"
        style={{ maxHeight: "calc(100vh - 280px)" }}
      >
        {filteredEmployees.length === 0 ? (
          // Empty State
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <User className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {searchQuery
                ? "No employees match your search"
                : "No Employees Found"}
            </h3>
            <p className="text-sm text-gray-500 text-center max-w-sm">
              {searchQuery
                ? "Try a different search term"
                : "Click the + button to add your first employee"}
            </p>
          </div>
        ) : (
          // Employee Cards Grid
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredEmployees.map((employee) => {
              const salaryInfo = getSalaryDisplay(employee);
              const SalaryIcon = salaryInfo.icon;

              return (
                <div
                  key={employee.id}
                  onClick={() => handleEmployeeClick(employee)}
                  className="bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition-all cursor-pointer border border-gray-100 hover:border-blue-200 group"
                >
                  {/* Avatar & Header */}
                  <div className="flex flex-col items-center mb-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                      <User className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="font-semibold text-gray-900 text-center text-base line-clamp-1">
                      {employee.name}
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">
                      ID: {employee.id}
                    </p>
                  </div>

                  {/* Type Badge */}
                  <div className="flex justify-center mb-4">
                    <div
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${
                        employee.type === 2
                          ? "bg-orange-50 text-orange-600"
                          : "bg-blue-50 text-blue-600"
                      }`}
                    >
                      {employee.type === 2 ? (
                        <Briefcase className="w-3.5 h-3.5" />
                      ) : (
                        <User className="w-3.5 h-3.5" />
                      )}
                      <span>
                        {employee.type === 2 ? "Manager" : "Employee"}
                      </span>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="border-t border-gray-100 mb-4"></div>

                  {/* Salary Info */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: salaryInfo.color + "15" }}
                      >
                        <SalaryIcon
                          className="w-4 h-4"
                          style={{ color: salaryInfo.color }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-500">
                          {salaryInfo.type}
                        </p>
                        <p
                          className="font-bold text-sm truncate"
                          style={{ color: salaryInfo.color }}
                        >
                          ${salaryInfo.amount.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* View Statistics Link */}
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <button className="w-full text-sm font-medium text-blue-600 hover:text-blue-700 transition">
                      View Statistics →
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Loading Overlay */}
      {refreshing && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-sm text-gray-600">Loading employees...</p>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
        .line-clamp-1 {
          overflow: hidden;
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 1;
        }
      `}</style>
    </div>
  );
}
